import {
  useClimateMetrics,
  Metric,
  useClimateHazards,
  MetricExportJobSummary,
  useMetricExportJobs,
} from '../hooks/analytics'
import { useLocationGroups, useLocations } from '../hooks/locations'
import { api } from 'balkerne-core/api'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { ago } from '../core/time'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import RBPopover from 'react-bootstrap/Popover'
import Page from '../components/Page'

// -------------------- Types -------------------- //

type EmissionTier = 'low' | 'moderate' | 'high'
type CanonicalPeriod = 'short' | 'mid' | 'long'

const canonicalPeriodOptions: CanonicalPeriod[] = ['short', 'mid', 'long']
const canonicalPeriodLabels: Record<CanonicalPeriod, string> = {
  short: 'Short term (2030)',
  mid: 'Mid term (2050)',
  long: 'Long term (2100)',
}

interface Hazard {
  id: number
  name: string // e.g. 'fld'
  display_name: string // e.g. 'Flood'
}

// Backend export request types
interface MetricExportConfig {
  metric_id?: number
  metric_key?: { hazard: string; code: string }
  use_defaults: boolean
  facet_filters?: Record<string, unknown> | null
}

interface ExportMetricsParams {
  property_ids?: number[]
  group_id?: number
  metrics: MetricExportConfig[]
  emission_tiers?: EmissionTier[] | null
  canonical_periods?: CanonicalPeriod[] | null
}

type FacetScalarValue = string | boolean
type FacetValues = Record<string, FacetScalarValue[]>

type MetricSelection = {
  id: number
  selected: boolean
  useDefaults: boolean // hidden toggle: true by default; becomes false when user saves facet overrides
  facetValues: FacetValues // when useDefaults is true, this should be {} so we don't send overrides
}

type HazardMetricGroup = {
  id: number
  label: string
  order: number
  metrics: Metric[]
}

function toStringList(value: unknown): string[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const normalized = list.map(v => String(v).trim()).filter(v => v.length > 0)

  return Array.from(new Set(normalized))
}

function toBooleanList(value: unknown): boolean[] {
  const list = Array.isArray(value) ? value : value != null ? [value] : []
  const normalized = list
    .map(v => {
      if (typeof v === 'boolean') return v
      if (typeof v === 'string') {
        const lowered = v.trim().toLowerCase()
        if (lowered === 'true') return true
        if (lowered === 'false') return false
      }
      return null
    })
    .filter((v): v is boolean => v !== null)

  return Array.from(new Set(normalized))
}

function coerceDefaultsForFacetLists(metric: Metric): FacetValues {
  const out: FacetValues = {}
  const schema = metric.facet_schema || {}
  const defs = metric.facet_defaults || {}

  for (const [key, def] of Object.entries(schema)) {
    const d = defs[key]
    if (def.type === 'bool') out[key] = toBooleanList(d)
    else out[key] = toStringList(d)
  }
  return out
}

type FacetDisplayRow = {
  key: string
  label: string
  values: string[]
}

const facetLabelByKey: Record<string, string> = {
  statistic: 'Statistic',
  variable: 'Variable',
  threshold: 'Threshold',
  target: 'Target',
  source: 'Source',
  defended: 'Defense',
  horizon: 'Horizon',
  member: 'Member',
}

function titleize(text: string): string {
  return text
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function presentFacetValue(value: string): string {
  const normalized = value.replace(/[_-]+/g, ' ').trim()
  if (!normalized) return value
  return normalized === normalized.toLowerCase() ? titleize(normalized) : normalized
}

function toFacetDisplayRows(metric: Metric, values: FacetValues): FacetDisplayRow[] {
  const schema = metric.facet_schema || {}
  const rows: FacetDisplayRow[] = []

  for (const [key, def] of Object.entries(schema)) {
    const raw = values[key]
    if (raw === undefined) continue

    if (def.type === 'bool') {
      const bools = toBooleanList(raw)
      if (!bools.length) continue
      rows.push({
        key,
        label: facetLabelByKey[key] ?? titleize(key),
        values: bools.map(v => {
          if (key === 'defended') return v ? 'Defended' : 'Undefended'
          return v ? 'True' : 'False'
        }),
      })
      continue
    }

    const strings = toStringList(raw)
    if (!strings.length) continue
    rows.push({
      key,
      label: facetLabelByKey[key] ?? titleize(key),
      values: strings.map(presentFacetValue),
    })
  }

  return rows
}

function cleanseFacetFiltersForPayload(values: FacetValues, metric: Metric): Record<string, unknown> | undefined {
  const cleaned: Record<string, unknown> = {}
  const schema = metric.facet_schema || {}
  for (const [k, def] of Object.entries(schema)) {
    const raw = values[k]
    if (raw === undefined) continue

    if (def.type === 'bool') {
      const arr = toBooleanList(raw)
      if (arr.length) cleaned[k] = arr
      continue
    }

    const arr = toStringList(raw)
    if (arr.length) cleaned[k] = arr
  }
  return Object.keys(cleaned).length ? cleaned : undefined
}

function FacetDialog({
  open,
  metric,
  initialValues,
  onCancel,
  onSave,
  onClear,
}: {
  open: boolean
  metric: Metric
  initialValues: FacetValues
  onCancel: () => void
  onSave: (nextValues: FacetValues) => void // caller sets useDefaults=false on save
  onClear: () => void // caller sets useDefaults=true and clears values
}) {
  const [values, setValues] = useState<FacetValues>({})

  useEffect(() => {
    setValues(initialValues)
  }, [initialValues, open])

  const schema = metric.facet_schema || {}

  const handleField = (key: string, value: FacetScalarValue[]) => setValues(v => ({ ...v, [key]: value }))

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Configure filters</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {Object.entries(schema).length === 0 && (
            <Alert severity="info">This metric has no configurable facets.</Alert>
          )}
          {Object.entries(schema).map(([key, def]) => {
            const raw = values[key]
            if (def.type === 'enum') {
              const arr = toStringList(raw)
              return (
                <FormControl key={key} fullWidth>
                  <InputLabel id={`${metric.id}-${key}-label`}>{key}</InputLabel>
                  <Select
                    multiple
                    labelId={`${metric.id}-${key}-label`}
                    label={key}
                    value={arr}
                    onChange={e => handleField(key, toStringList(e.target.value))}
                    renderValue={selected => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(selected as string[]).map(opt => (
                          <Chip key={opt} size="small" label={opt} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Stack>
                    )}>
                    {(def.values ?? []).map(opt => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={arr.includes(opt)} />
                        <Typography sx={{ ml: 1 }}>{opt}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            }
            if (def.type === 'bool') {
              const arr = toBooleanList(raw)
              const toggle = (boolValue: boolean, checked: boolean) => {
                if (checked) handleField(key, Array.from(new Set([...arr, boolValue])))
                else
                  handleField(
                    key,
                    arr.filter(v => v !== boolValue),
                  )
              }
              return (
                <FormControl key={key}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {key}
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox checked={arr.includes(true)} onChange={(_, checked) => toggle(true, checked)} />
                      }
                      label="True"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox checked={arr.includes(false)} onChange={(_, checked) => toggle(false, checked)} />
                      }
                      label="False"
                    />
                  </FormGroup>
                </FormControl>
              )
            }
            // text
            return (
              <Autocomplete<string, true, false, true>
                key={key}
                multiple
                freeSolo
                fullWidth
                options={[]}
                value={toStringList(raw)}
                onChange={(_, next) => handleField(key, toStringList(next))}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                  ))
                }
                renderInput={params => <TextField {...params} label={key} placeholder="Type a value and press Enter" />}
              />
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClear} color="inherit">
          Reset to default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(values)}>
          Save custom
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function MetricRow({
  metric,
  hazardLabel,
  selection,
  onChange,
}: {
  metric: Metric
  hazardLabel: string
  selection: MetricSelection
  onChange: (next: MetricSelection) => void
}) {
  const [open, setOpen] = useState(false)
  const defaultFacetValues = useMemo(() => coerceDefaultsForFacetLists(metric), [metric])
  const hasConfigurableFacets = useMemo(() => Object.keys(metric.facet_schema || {}).length > 0, [metric])
  const facetDetails = useMemo(
    () => toFacetDisplayRows(metric, selection.useDefaults ? defaultFacetValues : selection.facetValues),
    [metric, selection.useDefaults, selection.facetValues, defaultFacetValues],
  )
  const facetOverrideDetails = useMemo(
    () => (!selection.useDefaults ? toFacetDisplayRows(metric, selection.facetValues) : []),
    [metric, selection.useDefaults, selection.facetValues],
  )

  const handleOpen = () => {
    // If we are using defaults, seed local state with default facet arrays before opening.
    if (selection.useDefaults) {
      onChange({ ...selection, facetValues: defaultFacetValues })
    }
    setOpen(true)
  }

  const handleSave = (vals: FacetValues) => {
    onChange({ ...selection, useDefaults: false, facetValues: vals })
    setOpen(false)
  }

  const handleClear = () => {
    // revert to defaults mode; clear facet values so payload won't include overrides
    onChange({ ...selection, useDefaults: true, facetValues: {} })
    setOpen(false)
  }

  const metricInfoContent = (
    <Box sx={{ maxWidth: 420 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {hazardLabel} - {metric.label}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {metric.unit ? <Chip size="small" variant="outlined" label={`${metric.unit}`} /> : null}
        {metric.uses_return_periods ? <Chip size="small" variant="outlined" label="Return Periods" /> : null}
      </Stack>
      {metric.description ? (
        <Box
          sx={{
            ml: 1,
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: 'grey.100',
          }}>
          <Typography variant="body2" color="text.primary">
            {metric.description}
          </Typography>
        </Box>
      ) : null}
      {facetDetails.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {selection.useDefaults ? 'Facet defaults' : 'Facet overrides'}
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {facetDetails.map(row => (
              <Stack key={`${metric.id}-${row.key}`} direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {row.label}:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {row.values.map(value => (
                    <Chip key={`${metric.id}-${row.key}-${value}`} size="small" variant="outlined" label={value} />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  )

  const metricInfoOverlay = (
    <RBPopover id={`metric-info-${metric.id}`} className="metric-info-popover">
      <RBPopover.Content>{metricInfoContent}</RBPopover.Content>
    </RBPopover>
  )

  return (
    <Box
      key={metric.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1,
        px: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: metric.highlight ? 'primary.light' : 'transparent',
        backgroundColor: metric.highlight ? 'action.hover' : 'transparent',
      }}>
      <Checkbox
        checked={selection.selected}
        onChange={e => onChange({ ...selection, selected: e.target.checked })}
        sx={{ mr: 1 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography variant="body1">{metric.label}</Typography>
          <OverlayTrigger
            trigger={['hover', 'focus', 'click']}
            placement="right"
            rootClose
            delay={{ show: 120, hide: 100 }}
            overlay={metricInfoOverlay}>
            <IconButton
              size="small"
              aria-label={`Metric information for ${metric.label}`}
              sx={{ p: 0.25, color: 'text.secondary' }}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </OverlayTrigger>
          {metric.unit ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {metric.unit}
            </Typography>
          ) : null}
          {metric.uses_return_periods ? <Chip size="small" variant="outlined" label="Return Periods" /> : null}
        </Stack>
      </Box>

      {facetOverrideDetails.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mr: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          {facetOverrideDetails.map(row => (
            <Chip
              key={`${metric.id}-inline-${row.key}`}
              size="small"
              variant="outlined"
              label={`${row.label}: ${row.values.join(', ')}`}
            />
          ))}
        </Stack>
      )}

      {hasConfigurableFacets ? (
        <Button size="small" color="inherit" sx={{ color: 'text.secondary', minWidth: 'auto' }} onClick={handleOpen}>
          Configure
        </Button>
      ) : null}

      {hasConfigurableFacets ? (
        <FacetDialog
          open={open}
          metric={metric}
          initialValues={selection.useDefaults ? defaultFacetValues : selection.facetValues}
          onCancel={() => setOpen(false)}
          onSave={handleSave}
          onClear={handleClear}
        />
      ) : null}
    </Box>
  )
}

function MetricsExportPage() {
  const { enqueueSnackbar } = useSnackbar()

  const { data: hazards, isLoading: hzLoading, error: hzError } = useClimateHazards()
  const { data: metrics, isLoading: mLoading, error: mError } = useClimateMetrics()
  const { data: locationGroups, isLoading: groupsLoading } = useLocationGroups()
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const { data: locations, isLoading: locLoading } = useLocations(selectedGroupId ? { group_id: selectedGroupId } : {})
  const metricExportJobsQuery = useMetricExportJobs()

  const [exportAllProps, setExportAllProps] = useState<boolean>(true) // default ON
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
  const [tiers, setTiers] = useState<EmissionTier[]>(['low', 'moderate', 'high'])
  const [allPeriods, setAllPeriods] = useState<boolean>(false)
  const [canonicalPeriods, setCanonicalPeriods] = useState<CanonicalPeriod[]>(canonicalPeriodOptions)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string>('')
  const [exportsOpen, setExportsOpen] = useState<boolean>(false)
  const [exportsPage, setExportsPage] = useState<number>(1)

  const hazardItems = useMemo(() => hazards ?? [], [hazards])
  const metricItems = useMemo(() => metrics ?? [], [metrics])
  const locationGroupItems = useMemo(() => locationGroups ?? [], [locationGroups])
  const locationItems = useMemo(() => locations ?? [], [locations])

  // metricId -> selection
  const [selections, setSelections] = useState<Record<number, MetricSelection>>({})

  // Initialize selections when metrics arrive
  useEffect(() => {
    if (metricItems.length === 0) return
    setSelections(prev => {
      const next: Record<number, MetricSelection> = { ...prev }
      metricItems.forEach(m => {
        if (!next[m.id]) {
          next[m.id] = {
            id: m.id,
            selected: false,
            useDefaults: true,
            facetValues: {}, // keep empty until user configures; defaults are injected in dialog
          }
        }
      })
      return next
    })
  }, [metricItems])

  useEffect(() => {
    setSelectedPropertyIds(prev => prev.filter(id => locationItems.some((location: any) => location.id === id)))
  }, [locationItems])

  const hazardLookup = useMemo(() => {
    const map = new Map<string, Hazard>()
    hazardItems.forEach(h => map.set(h.name, h))
    return map
  }, [hazardItems])

  const metricsByHazardGroup = useMemo(() => {
    const byHazard = new Map<string, Map<number, HazardMetricGroup>>()

    metricItems.forEach(metric => {
      if (metric.hide) {
        return
      }

      const metricGroup = metric.group
      const hazardGroups = byHazard.get(metric.hazard) || new Map<number, HazardMetricGroup>()

      if (!hazardGroups.has(metricGroup.id)) {
        hazardGroups.set(metricGroup.id, {
          id: metricGroup.id,
          label: metricGroup.label,
          order: metricGroup.order,
          metrics: [],
        })
      }

      hazardGroups.get(metricGroup.id)?.metrics.push(metric)
      byHazard.set(metric.hazard, hazardGroups)
    })

    const orderedByHazard = new Map<string, HazardMetricGroup[]>()
    for (const [hazard, groupsById] of byHazard.entries()) {
      const orderedGroups = Array.from(groupsById.values())
      orderedGroups.forEach(group => {
        group.metrics.sort((left, right) => {
          if (left.order !== right.order) {
            return left.order - right.order
          }
          return left.label.localeCompare(right.label)
        })
      })

      orderedGroups.sort((left, right) => {
        if (left.order !== right.order) {
          return left.order - right.order
        }
        return left.label.localeCompare(right.label)
      })

      orderedByHazard.set(hazard, orderedGroups)
    }

    return orderedByHazard
  }, [metricItems])

  const selectedMetrics: Metric[] = useMemo(() => {
    return metricItems.filter(metric => !metric.hide && selections[metric.id]?.selected)
  }, [metricItems, selections])
  const selectedMetricCountByHazard = useMemo(() => {
    const counts = new Map<string, number>()
    selectedMetrics.forEach(metric => {
      counts.set(metric.hazard, (counts.get(metric.hazard) ?? 0) + 1)
    })
    return counts
  }, [selectedMetrics])
  const selectedMetricCount = selectedMetrics.length
  const selectedPropertyCount = useMemo(
    () => (exportAllProps ? locationItems.length : selectedPropertyIds.length),
    [exportAllProps, locationItems, selectedPropertyIds],
  )
  const exportDisabled = submitState === 'submitting' || selectedMetricCount === 0

  const exportJobs = metricExportJobsQuery.data ?? []
  const exportCount = exportJobs.length
  const hasActiveExportJobs = exportJobs.some(j => j.status === 'QUEUED' || j.status === 'PROCESSING')

  const EXPORTS_PAGE_SIZE = 5
  const exportsPageCount = Math.max(1, Math.ceil(exportCount / EXPORTS_PAGE_SIZE))
  const exportsPageSafe = Math.min(exportsPage, exportsPageCount)
  const exportsPageJobs = exportJobs.slice(
    (exportsPageSafe - 1) * EXPORTS_PAGE_SIZE,
    exportsPageSafe * EXPORTS_PAGE_SIZE,
  )

  useEffect(() => {
    if (hasActiveExportJobs) {
      setExportsOpen(true)
      setExportsPage(1)
    }
  }, [hasActiveExportJobs])

  useEffect(() => {
    setExportsPage(prev => Math.min(prev, exportsPageCount))
  }, [exportsPageCount])

  const handleSelectionChange = (metricId: number, next: MetricSelection) => {
    setSelections(s => ({ ...s, [metricId]: next }))
  }

  const formatBytes = (bytes?: number | null) => {
    if (!bytes) return null
    const mb = bytes / (1024 * 1024)
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(2)} GB`
  }

  const statusChip = (status: MetricExportJobSummary['status']) => {
    if (status === 'COMPLETE') return { label: 'Complete', color: 'success' as const, variant: 'filled' as const }
    if (status === 'FAILED') return { label: 'Failed', color: 'error' as const, variant: 'filled' as const }
    if (status === 'PROCESSING') return { label: 'Processing', color: 'warning' as const, variant: 'outlined' as const }
    return { label: 'Queued', color: 'default' as const, variant: 'outlined' as const }
  }

  const handleDownload = async (jobId: string) => {
    try {
      const res = await api.get(`/analytics/cc/metrics/exports/${jobId}/download`, {
        params: { redirect: false },
      })
      const url = res?.data?.url
      if (!url) {
        enqueueSnackbar('Download URL not available yet.', { variant: 'warning' })
        return
      }
      window.location.assign(url)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 409) enqueueSnackbar('Export is not ready yet.', { variant: 'info' })
      else if (status === 410) enqueueSnackbar('Export has expired.', { variant: 'warning' })
      else enqueueSnackbar('Failed to start download.', { variant: 'error' })
    }
  }

  const handleExport = async () => {
    const allPropsIds = locationItems.map((property: any) => property.id)
    const propertyIdsForPayload = exportAllProps ? allPropsIds : selectedPropertyIds

    if (!propertyIdsForPayload.length) {
      setSubmitError('No properties available to export.')
      setSubmitState('error')
      return
    }
    if (!selectedMetrics.length) {
      setSubmitError('Please select at least one metric to export.')
      setSubmitState('error')
      return
    }
    if (!allPeriods && canonicalPeriods.length === 0) {
      setSubmitError('Select at least one canonical period or enable all periods.')
      setSubmitState('error')
      return
    }

    const metricsConfig: MetricExportConfig[] = selectedMetrics.map(m => {
      const sel = selections[m.id]
      const filters = !sel.useDefaults ? cleanseFacetFiltersForPayload(sel.facetValues, m) : undefined
      return {
        metric_id: m.id,
        use_defaults: sel.useDefaults,
        facet_filters: filters,
      }
    })

    const payload: ExportMetricsParams = {
      ...(exportAllProps && selectedGroupId ? { group_id: selectedGroupId } : { property_ids: propertyIdsForPayload }),
      metrics: metricsConfig,
      emission_tiers: tiers.length ? tiers : undefined,
      canonical_periods: allPeriods ? undefined : canonicalPeriods,
    }

    try {
      setSubmitState('submitting')
      setSubmitError('')
      await api.post('/analytics/cc/metrics/export', payload)
      setSubmitState('success')
      enqueueSnackbar('Export queued. It will appear under “Recent exports”.', { variant: 'success' })
      metricExportJobsQuery.refetch()
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || err?.message || 'Export failed')
      setSubmitState('error')
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
    }
  }
  const emissionOptions: EmissionTier[] = ['high', 'moderate', 'low']
  const toggleTier = (tier: EmissionTier) =>
    setTiers(prev => (prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]))
  const toggleCanonicalPeriod = (period: CanonicalPeriod) =>
    setCanonicalPeriods(prev => (prev.includes(period) ? prev.filter(p => p !== period) : [...prev, period]))
  const loadingAny = hzLoading || mLoading || locLoading || groupsLoading

  const selectedGroup = useMemo(
    () => locationGroupItems.find((group: any) => group.id === selectedGroupId) ?? null,
    [locationGroupItems, selectedGroupId],
  )

  const handleGroupChange = (_: unknown, value: any) => {
    setSelectedGroupId(value?.id ?? null)
    setSelectedPropertyIds([])
  }

  const handleClearSelections = () => {
    setSelectedGroupId(null)
    setSelectedPropertyIds([])
    setExportAllProps(true)
    setTiers(['low', 'moderate', 'high'])
    setAllPeriods(false)
    setCanonicalPeriods(canonicalPeriodOptions)
    setSelections(s => {
      const next = { ...s }
      Object.keys(next).forEach(k => {
        const key = Number(k)
        next[key] = {
          ...next[key],
          selected: false,
          useDefaults: true,
          facetValues: {},
        }
      })
      return next
    })
    setSubmitState('idle')
    setSubmitError('')
  }

  return (
    <Page title="Climate Analytics Export" back>
      <Box p={1}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">Configure Export</Typography>
            </Stack>
          </Stack>

          {loadingAny && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2">Loading…</Typography>
            </Stack>
          )}

          {!loadingAny && !hazards?.length && !mError && (
            <Alert severity="info">No climate hazards or metrics available.</Alert>
          )}

          {submitState === 'error' && <Alert severity="error">{submitError}</Alert>}
          {submitState === 'success' && <Alert severity="success">Export queued.</Alert>}

          <Accordion variant="outlined" expanded={exportsOpen} onChange={(_, expanded) => setExportsOpen(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Recent exports
                </Typography>
                {exportCount > 0 && <Chip size="small" label={exportCount} color="primary" />}
                <Box sx={{ flex: 1 }} />
                {metricExportJobsQuery.isFetching && <CircularProgress size={16} />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {metricExportJobsQuery.isLoading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading exports…</Typography>
                </Stack>
              )}

              {!metricExportJobsQuery.isLoading && exportCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No recent exports yet.
                </Typography>
              )}

              {exportCount > 0 && (
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" color="inherit" onClick={() => metricExportJobsQuery.refetch()}>
                      Refresh
                    </Button>
                    {hasActiveExportJobs && (
                      <Typography variant="body2" color="text.secondary">
                        Updating…
                      </Typography>
                    )}
                  </Stack>
                  {exportsPageJobs.map(job => {
                    const chip = statusChip(job.status)
                    return (
                      <Box
                        key={job.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          flexWrap: 'wrap',
                        }}>
                        <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>
                          Requested {ago(job.created_at)}
                          {job.file_size_bytes ? (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}
                              • {formatBytes(job.file_size_bytes)}
                            </Typography>
                          ) : null}
                        </Typography>
                        {job.status === 'FAILED' && job.error && (
                          <Typography variant="body2" color="error" sx={{ flexBasis: '100%' }}>
                            {job.error}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          disabled={job.status !== 'COMPLETE'}
                          onClick={() => handleDownload(job.id)}>
                          Download
                        </Button>
                      </Box>
                    )
                  })}

                  {exportsPageCount > 1 && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Pagination
                        size="small"
                        count={exportsPageCount}
                        page={exportsPageSafe}
                        onChange={(_, page) => setExportsPage(page)}
                      />
                    </Stack>
                  )}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          <Card variant="outlined">
            <CardHeader title="Scope" />
            <CardContent>
              <Stack spacing={2.5}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Properties
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedPropertyCount} selected`}
                      color={selectedPropertyCount ? 'primary' : 'default'}
                      variant={selectedPropertyCount ? 'filled' : 'outlined'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Pick a property portfolio to select locations from. No group defaults to the whole portfolio.
                  </Typography>
                  <Stack spacing={1.5} direction="column">
                    <Autocomplete
                      options={locationGroupItems}
                      getOptionLabel={o => o.name}
                      value={selectedGroup}
                      onChange={handleGroupChange}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => <TextField {...params} label="Property group" placeholder="All" />}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', width: '100%' }}>
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            {option.name}
                          </Typography>
                          {option.property_customer_reference && (
                            <Typography variant="caption" color="text.secondary">
                              {option.property_customer_reference}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    <hr />
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormGroup sx={{ m: 0, flexShrink: 0 }}>
                        <FormControlLabel
                          control={<Switch checked={exportAllProps} onChange={(_, v) => setExportAllProps(v)} />}
                          label="Include all"
                        />
                      </FormGroup>
                      <Autocomplete
                        multiple
                        fullWidth
                        disableCloseOnSelect
                        options={locationItems.map((property: any) => ({ id: property.id, name: property.name }))}
                        getOptionLabel={o => o.name}
                        value={locationItems
                          .filter((property: any) => selectedPropertyIds.includes(property.id))
                          .map((property: any) => ({ id: property.id, name: property.name }))}
                        onChange={(_, vals) => setSelectedPropertyIds(vals.map(v => v.id))}
                        renderInput={params => (
                          <TextField {...params} label="Properties" placeholder="Select properties" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip variant="outlined" label={option.name} {...getTagProps({ index })} key={option.id} />
                          ))
                        }
                        disabled={exportAllProps}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Emission tiers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Select the emissions pathways to include in the export.
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {emissionOptions.map(t => {
                      const selected = tiers.includes(t)
                      return (
                        <Chip
                          key={t}
                          label={t.charAt(0).toUpperCase() + t.slice(1)}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => toggleTier(t)}
                          sx={{ minWidth: 96 }}
                        />
                      )
                    })}
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Periods
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Export all available periods, or select from standard horizons.
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <FormGroup sx={{ m: 0, flexShrink: 0 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={allPeriods}
                            onChange={(_, checked) => {
                              setAllPeriods(checked)
                              setCanonicalPeriods(checked ? [] : canonicalPeriodOptions)
                            }}
                          />
                        }
                        label="All periods"
                      />
                    </FormGroup>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                      {canonicalPeriodOptions.map(period => {
                        const selected = canonicalPeriods.includes(period)
                        return (
                          <Chip
                            key={period}
                            label={canonicalPeriodLabels[period]}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            disabled={allPeriods}
                            onClick={() => toggleCanonicalPeriod(period)}
                            sx={{ minWidth: 144 }}
                          />
                        )
                      })}
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardHeader title="Metrics" />
            <CardContent sx={{ p: 0 }}>
              {Array.from(metricsByHazardGroup.entries()).map(([hazardKey, groups]) => (
                <Accordion key={hazardKey}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="h6">{hazardLookup.get(hazardKey)?.display_name || hazardKey}</Typography>
                      {(selectedMetricCountByHazard.get(hazardKey) ?? 0) > 0 ? (
                        <Chip size="small" color="primary" label={selectedMetricCountByHazard.get(hazardKey)} />
                      ) : null}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <Stack spacing={0.5}>
                      {groups.map(group => (
                        <Box key={`${hazardKey}-${group.id}`}>
                          <Box
                            sx={{
                              px: 1,
                              py: 0.75,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: 'grey.100',
                            }}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {group.label}
                            </Typography>
                          </Box>
                          <Box>
                            {group.metrics.map(metric => (
                              <MetricRow
                                key={metric.id}
                                metric={metric}
                                hazardLabel={hazardLookup.get(hazardKey)?.display_name || hazardKey}
                                selection={
                                  selections[metric.id] || {
                                    id: metric.id,
                                    selected: false,
                                    useDefaults: true,
                                    facetValues: {},
                                  }
                                }
                                onChange={next => handleSelectionChange(metric.id, next)}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </CardContent>
          </Card>

          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-start">
            <Button variant="contained" onClick={handleExport} disabled={exportDisabled} sx={{ minWidth: 120 }}>
              {submitState === 'submitting' ? 'Exporting…' : 'Export'}
            </Button>
            <Button variant="text" onClick={handleClearSelections}>
              Clear selection
            </Button>
            {/* <Chip
              label={`${selectedMetricCount} metric${selectedMetricCount === 1 ? '' : 's'} selected`}
              color={selectedMetricCount ? 'primary' : 'default'}
              variant={selectedMetricCount ? 'filled' : 'outlined'}
            /> */}
          </Stack>
        </Stack>
      </Box>
    </Page>
  )
}

export default MetricsExportPage
